import { useEffect, useState } from "react";
import { Menu, X, ArrowRight } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import { LOGOS, TIDYCAL } from "@/lib/images";

const BOOK_URL = TIDYCAL.clarity;

export default function Header() {
  const { t, lang, setLang } = useT();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const NAV = [
    { href: "#top", label: t.nav.home, key: "home" },
    { href: "#sobre", label: t.nav.about, key: "about" },
    { href: "#ayuda", label: t.nav.help, key: "help" },
    { href: "#sesiones", label: t.nav.sessions, key: "sessions" },
    { href: "#contacto", label: t.nav.contact, key: "contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 vk-header-blur transition-all duration-300 ${
        scrolled ? "border-b vk-line" : "border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between gap-4">
        <a
          href="#top"
          data-testid="logo-link"
          aria-label="VeronikaconK"
          className="flex items-center whitespace-nowrap"
        >
          <img
            src={LOGOS.horizontal}
            alt="VeronikaconK — Clarity today. Freedom tomorrow."
            className="h-14 md:h-16 w-auto object-contain"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <a
              key={n.key}
              href={n.href}
              data-testid={`nav-${n.key}`}
              className="vk-underline text-[12px] uppercase tracking-[0.18em] font-medium"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <LanguageToggle lang={lang} setLang={setLang} />
          <a
            href={BOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-cta-button"
            className="group inline-flex items-center gap-2 bg-[#4B2F4F] text-[#F7F3F7] px-5 py-3 text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-[#E0B25B] transition-colors duration-300"
          >
            {t.header.cta}
            <ArrowRight
              size={14}
              strokeWidth={1.4}
              className="group-hover:translate-x-0.5 transition-transform"
            />
          </a>
        </div>

        <div className="lg:hidden flex items-center gap-3">
          <LanguageToggle lang={lang} setLang={setLang} compact />
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className="p-2 -mr-2"
            aria-label="Menu"
          >
            {open ? <X strokeWidth={1.4} /> : <Menu strokeWidth={1.4} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t vk-line bg-[#F7F3F7]" data-testid="mobile-menu">
          <div className="px-6 py-8 flex flex-col gap-5">
            {NAV.map((n) => (
              <a
                key={n.key}
                href={n.href}
                onClick={() => setOpen(false)}
                data-testid={`mnav-${n.key}`}
                className="text-base uppercase tracking-[0.18em] font-medium"
              >
                {n.label}
              </a>
            ))}
            <a
              href={BOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              data-testid="mobile-cta-button"
              className="bg-[#4B2F4F] text-[#F7F3F7] px-6 py-4 text-[12px] uppercase tracking-[0.22em] mt-2 inline-flex items-center justify-center gap-2"
            >
              {t.header.cta}
              <ArrowRight size={14} strokeWidth={1.4} />
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

function LanguageToggle({ lang, setLang, compact = false }) {
  return (
    <div
      data-testid="lang-switcher"
      className={`flex items-center gap-1 border vk-line-strong ${compact ? "p-0.5" : "p-1"}`}
      role="group"
      aria-label="Language switcher"
    >
      {["es", "en"].map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => setLang(opt)}
          data-testid={`lang-btn-${opt}`}
          className={`text-[11px] uppercase tracking-[0.18em] font-medium px-2.5 py-1 transition-colors duration-300 ${
            lang === opt
              ? "bg-[#4B2F4F] text-[#F7F3F7]"
              : "text-[#4B2F4F] hover:bg-[#4B2F4F]/5"
          }`}
          aria-pressed={lang === opt}
        >
          {opt.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
